import { cx } from '@twind/core';
import { useEffect, useState } from 'react';

export type InputConfig = {
  type?: string;
  maxLength?: number;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel' | 'url' | 'search';
  autoComplete?: 'on' | 'off';
  pattern?: string;
};

export interface InputProps {
  id: string;
  name?: string;
  label?: string;
  value?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | string[];
  showError?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  inputClassName?: string;
  labelClassName?: string;
  errorClassName?: string;
  customConfig?: Partial<InputConfig>;
  minHeight?: string;
  maxHeight?: string;
  style?: React.CSSProperties;
  onLabelClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  errorId?: string;
}

export default function Input({
  id,
  name,
  label,
  value = '',
  placeholder,
  required = false,
  disabled = false,
  error,
  showError = false,
  onChange,
  onBlur,
  onFocus,
  className = '',
  inputClassName = '',
  labelClassName = '',
  errorClassName = '',
  customConfig = { type: 'text' },
  minHeight,
  maxHeight,
  style,
  onLabelClick,
  errorId,
}: InputProps) {
  const [internalValue, setInternalValue] = useState(value);
  const isMultiLine = customConfig?.type === 'textarea';
  const InputComponent = isMultiLine ? 'textarea' : 'input';

  const inputConfig = {
    ...customConfig,
  };

  const hasErrors = !!error && (Array.isArray(error) ? error.length > 0 : !!error);
  const shouldShowError = showError && hasErrors;
  const errorText = Array.isArray(error) ? error.join(', ') : error;

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {label && (
        <label
          htmlFor={id}
          className={cx('flex text-sm gap-1', labelClassName)}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: label,
            }}
            onClick={onLabelClick}
          />
          {!required && <span>(optional)</span>}
        </label>
      )}
      <InputComponent
        id={id}
        name={name || id}
        required={required}
        disabled={disabled}
        placeholder={placeholder}
        value={internalValue}
        onChange={handleChange}
        onBlur={onBlur}
        onFocus={onFocus}
        {...inputConfig}
        className={cx(
          'px-[14px] py-3 border md:text-sm rounded-[8px]',
          shouldShowError
            ? 'border-[#FF3323] outline-[#FF3323]'
            : 'border-typography-divider outline-typography-divider',
          isMultiLine && 'min-h-[60px]',
          inputClassName
        )}
        style={{
          minHeight: isMultiLine ? minHeight || '60px' : undefined,
          maxHeight: isMultiLine ? maxHeight : undefined,
          ...style,
        }}
        aria-invalid={shouldShowError}
        aria-describedby={shouldShowError ? errorId : undefined}
      />
      {shouldShowError && (
        <div
          className={cx('text-xs -mt-1 text-[#FF3323]', errorClassName)}
          id={errorId}
        >
          {errorText}
        </div>
      )}
    </div>
  );
}
